import React, { useState } from 'react';
import { motion, useMotionValue, useTransform, animate } from 'framer-motion';
import { MailOpen, User, Sparkles, ExternalLink, Trash2, Bookmark, Share2, Lock } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { t } from '../translations';

/**
 * MobileArticleCard renderar ett artikelkort optimerat för touch och gester,
 * komplett med svepåtgärder och paywall-spärr när sammanfattning saknas och kvoten är uppnådd.
 */
const MobileArticleCard = ({ 
  item, 
  markAsRead, 
  deleteItem, 
  renderAISummary, 
  saveFromInbox, 
  onHide, 
  isLibraryView = false,
  canGenerateSummary = true,
  usageStats,
  consumeSummary, // NYTT: Skickas från InboxView -> App.js
  userProfile     // NYTT: Skickas från InboxView -> App.js
}) => {
  const { language } = useLanguage();
  const [revealed, setRevealed] = useState(false);

  // useMotionValue flyttar kortet utan att orsaka onödiga React-omrenderingar
  const x = useMotionValue(0);

  // Ikonernas synlighet baserat på kortets horisontella position under swipe
  const rightIconOpacity = useTransform(x, [10, 60], [0, 1]);
  const leftIconOpacity = useTransform(x, [-10, -60], [0, 1]);

  const executeArchive = (e) => {
    e?.stopPropagation();
    onHide(item.id); 
    saveFromInbox(item);
  };

  const executeDelete = (e) => {
    e?.stopPropagation();
    onHide(item.id); 
    deleteItem(item.id);
  };

  const handleCardClick = () => {
    if (revealed) {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
      setRevealed(false);
      return;
    }
    // Om användaren duttar på kortet markeras det som läst
    if (!item.is_read && !isLibraryView) {
      markAsRead(item.id);
    }
  };

  // När användaren släpper sveprörelsen med fingret
  const handleDragEnd = (event, info) => {
    const currentX = x.get(); 
    const velocity = info.velocity.x;

    // 1. Dra åt höger (Markera som läst)
    if (currentX > 50 || (velocity > 500 && currentX > 15)) {
      if (!item.is_read && !isLibraryView) markAsRead(item.id);
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
      setRevealed(false);
    } 
    // 2. Dra åt vänster (Visa Radera-knappen)
    else if (currentX < -50 || (velocity < -500 && currentX < -15)) {
      animate(x, -110, { type: 'spring', stiffness: 400, damping: 30 });
      setRevealed(true);
    } 
    // 3. Om man släpper för tidigt (Fjädra tillbaka till mitten)
    else {
      animate(x, 0, { type: 'spring', stiffness: 400, damping: 30 });
      setRevealed(false);
    }
  };

  // Öppnar betalväggen i App.js vid klick på knappen
  const handlePaywallClick = (e) => {
    e?.stopPropagation();
    if (consumeSummary) {
      consumeSummary();
    }
  };

  // Har artikeln redan en sparad AI-sammanfattning?
  const hasExistingSummary = Boolean(item.ai_summary || item.summary);

  // Paywall visas ENDAST om artikeln saknar sammanfattning och användaren saknar kvot
  const showPaywall = !hasExistingSummary && !canGenerateSummary && !isLibraryView;

  // Hämta korrekt månadskvot från usageStats (standard: 600 för basic)
  const maxLimit = usageStats?.limits?.summaries || 600;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, height: 0, marginBottom: 0, overflow: 'hidden' }}
      transition={{ duration: 0.3 }}
      className="relative mb-2 cursor-pointer w-full max-w-full box-border overflow-hidden"
    >
      {/* BAKGRUNDEN (Swipe-actions vid drag) */}
      <div className="absolute inset-0 bg-slate-100 rounded-3xl overflow-hidden z-0 w-full" onClick={handleCardClick}>
        <div className="absolute inset-0 flex justify-between items-center px-4">
          <motion.div style={{ opacity: rightIconOpacity }} className="flex items-center text-blue-600 font-bold">
            <div className="bg-blue-100 p-2 rounded-full mr-2 shrink-0">
              {isLibraryView ? <Share2 size={18} /> : <MailOpen size={18} />}
            </div>
            <span className="text-[10px] uppercase tracking-wider leading-tight">
              {isLibraryView ? t(language, 'library_share') : t(language, 'inbox_mark_read')}
            </span>
          </motion.div>

          <motion.div style={{ opacity: leftIconOpacity }} className="flex justify-end shrink-0">
             <button
               onClick={executeDelete}
               className="bg-red-500 text-white p-3 rounded-2xl shadow-lg active:scale-95 transition-all"
             >
               <Trash2 size={18} />
             </button>
          </motion.div>
        </div>
      </div>

      {/* FÖRGRUNDEN - Native Drag med touch-stöd */}
      <motion.div
        drag="x"
        dragDirectionLock={true}
        dragConstraints={{ left: -110, right: 80 }} 
        dragElastic={0.1}
        onDragEnd={handleDragEnd}
        style={{ x, touchAction: 'pan-y' }}
        onClick={handleCardClick}
        className="rounded-3xl border border-slate-200 text-left relative z-10 flex flex-col overflow-hidden bg-white shadow-sm w-full box-border"
      >
        <div className="p-4 border-b border-slate-50 w-full box-border min-w-0">
            <div className="flex justify-between items-start mb-2 gap-2">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <span className="text-[10px] font-bold text-slate-400 uppercase shrink-0">
                      {new Date(item.created_at || Date.now()).toLocaleDateString()}
                    </span>
                    {!item.is_read && !isLibraryView && (
                      <span className="bg-blue-600 text-white text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider animate-pulse shrink-0">
                        {t(language, 'inbox_new')}
                      </span>
                    )}
                </div>
            </div>
            
            <h3 className={`text-lg font-serif mb-2 leading-tight break-words min-w-0 ${item.is_read || isLibraryView ? 'text-slate-600 font-medium' : 'text-slate-900 font-bold'}`}>
                {item.article_title || item.title}
            </h3>
            
            <div className="space-y-1 mb-2">
                <p className="text-xs font-medium italic flex items-start gap-2 text-slate-500">
                  <User size={12} className="shrink-0 mt-0.5"/> 
                  <span className="leading-relaxed line-clamp-2 break-words">
                    {item.authors || item.author || t(language, 'inbox_unknown_author')}
                  </span>
                </p>
                <div className="text-[10px] uppercase tracking-tighter font-sans flex items-center gap-2 text-slate-400">
                  <span className="truncate min-w-0">
                    {item.venue || item.journal || t(language, 'inbox_unknown_journal')}
                  </span>
                </div>
            </div>
        </div>
        
        {/* AI-summering Sektion */}
        <div className="p-4 border-t border-indigo-100 bg-indigo-50/40 border-l-4 border-l-indigo-300 w-full box-border min-w-0 relative">
            <div className="flex items-center gap-2 text-[10px] font-bold text-indigo-500 uppercase tracking-widest mb-2">
              <Sparkles size={12} className="shrink-0"/> {t(language, 'inbox_ai_summary')}
            </div>

            {showPaywall ? (
              /* --- PAYWALL LÄGE FÖR MOBIL (Bara om sammanfattning saknas och kvoten är slut) --- */
              <div className="relative">
                <div className="leading-relaxed text-[14px] font-serif break-words text-slate-700 blur-[3px] select-none opacity-40">
                  {t(language, 'card_blurred_placeholder')}
                </div>
                
                <div className="absolute inset-0 flex flex-col items-center justify-center z-10 px-2 py-1">
                  <div className="bg-white/95 backdrop-blur-sm p-4 rounded-xl shadow-lg border border-indigo-100 w-full text-center">
                    <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <Lock size={18} />
                    </div>
                    <h4 className="text-sm font-bold text-slate-800 mb-1">{t(language, 'card_quota_title')}</h4>
                    <p className="text-xs text-slate-600 mb-3 leading-relaxed">
                      {t(language, 'card_quota_desc_mobile', { maxLimit })}
                    </p>
                    <button 
                      onClick={handlePaywallClick}
                      className="w-full bg-slate-900 text-white px-3 py-2.5 rounded-lg text-xs font-bold hover:bg-slate-800 transition-colors shadow-md active:scale-95"
                    >
                      {t(language, 'card_quota_btn')}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              /* --- NORMALT LÄGE: Visar alltid den sparade sammanfattningen --- */
              <>
                <div className="leading-relaxed whitespace-pre-line text-[14px] font-serif break-words text-slate-700">
                    {renderAISummary(item.ai_summary || item.summary || item.abstract, language)}
                </div>
                
                <div className="pt-3 mt-3 border-t border-indigo-100/50 flex flex-row justify-between items-center gap-2 w-full box-border">
                    <a 
                      href={item.doi || item.article_url} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="text-[10px] font-bold text-indigo-600 uppercase flex items-center gap-1 truncate min-w-0 flex-1"
                    >
                      <span className="truncate">{t(language, 'inbox_read_original')}</span> <ExternalLink size={10} className="shrink-0"/>
                    </a>
                    
                    {!isLibraryView && (
                      <button 
                        onClick={executeArchive}
                        className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-3 py-1.5 rounded-full uppercase flex items-center gap-1 shrink-0"
                      >
                        <Bookmark size={12} className="shrink-0"/> <span>{t(language, 'inbox_archive')}</span>
                      </button>
                    )}
                </div>
              </>
            )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MobileArticleCard;